import React from 'react';
import styled from '@emotion/styled';
import { DCard, DCardTitle, ExternalLabel, KvGrid, MutedText } from './detailStyles';
import { DetailInfoModel, SessionLine } from './detailInfo';
import CouponFee, { PaidFee, PaidPrice } from './CouponFee';
import type { BestCouponPrice } from './useSeminarCoupons';

/*
 * 모바일 "세미나 정보" 표 — 라벨 76px, 14px.
 * 커리어: 일정(회차형이면 아래 회차 목록) · 진행 방식 · (오프라인) 장소 · 참가비 · 모집 · (유료) 환불·제공 기간.
 * 일반: 일정 · 장소/온라인 · 참가비("외부 접수" 회색 라벨). 문의 행 없음.
 */

const CareerFee = styled.span`
  font-size: 20px;
  font-weight: 800;
  line-height: 1.3;
  font-variant-numeric: tabular-nums;
`;

const GeneralFee = styled.span`
  font-size: 16px;
  font-weight: 400;
  color: #374151;
  font-variant-numeric: tabular-nums;
`;

/** 환불 행 아래 한 줄(동영상 접속 후 환불 불가) */
const RefundNote = styled.span`
  display: block;
  margin-top: 2px;
  font-size: 13px;
  color: #b45309;
`;

const SessionListBox = styled.ol`
  list-style: none;
  margin: 6px 0 0;
  padding: 8px 10px;
  border-radius: 8px;
  background: #f9fafb;
  font-size: 13px;
  line-height: 1.6;
  color: #374151;

  li + li {
    margin-top: 2px;
  }
`;

export const SessionList: React.FC<{ sessions: SessionLine[] }> = ({ sessions }) => (
  <SessionListBox aria-label="회차 일정">
    {sessions.map((session: SessionLine) => (
      <li key={session.label}>
        {session.label}
        {session.title && <MutedText> · {session.title}</MutedText>}
      </li>
    ))}
  </SessionListBox>
);

interface DetailInfoCardProps {
  info: DetailInfoModel;
  /** 쿠폰 적용가(커리어 유료, 받은/받을 수 있는 쿠폰 중 최대 할인) — 없으면 정가만 */
  couponPrice?: BestCouponPrice | null;
  /** 이미 결제했으면 결제한 금액(쿠폰 적용가 대신) */
  paidPrice?: PaidPrice | null;
}

const DetailInfoCard: React.FC<DetailInfoCardProps> = ({ info, couponPrice, paidPrice }) => (
  <DCard aria-labelledby="seminar-info-title">
    <DCardTitle id="seminar-info-title">세미나 정보</DCardTitle>
    <KvGrid labelWidth={76}>
      <dt>일정</dt>
      <dd>
        {info.scheduleText}
        {info.sessions.length > 0 && <SessionList sessions={info.sessions} />}
      </dd>

      {info.isCareer && info.formatText && (
        <>
          <dt>진행 방식</dt>
          <dd>{info.formatText}</dd>
        </>
      )}

      {info.placeText && (
        <>
          <dt>장소</dt>
          <dd>{info.placeText}</dd>
        </>
      )}

      <dt>참가비</dt>
      <dd>
        {info.isCareer && paidPrice ? (
          <PaidFee paid={paidPrice} />
        ) : info.isCareer && couponPrice ? (
          <CouponFee best={couponPrice} listPrice={info.fee} claimHint="쿠폰은 아래에서 받을 수 있어요" />
        ) : info.isCareer ? (
          <CareerFee>{info.feeText}</CareerFee>
        ) : (
          <>
            <GeneralFee>{info.feeText}</GeneralFee>
            <ExternalLabel>외부 접수</ExternalLabel>
          </>
        )}
      </dd>

      {info.recruitText && (
        <>
          <dt>모집</dt>
          <dd>{info.recruitText}</dd>
        </>
      )}

      {info.refundText && (
        <>
          <dt>환불</dt>
          <dd>
            {info.refundText}
            {info.refundNote && <RefundNote>{info.refundNote}</RefundNote>}
          </dd>
        </>
      )}

      {info.provideText && (
        <>
          <dt>제공 기간</dt>
          <dd>{info.provideText}</dd>
        </>
      )}
    </KvGrid>
  </DCard>
);

export default DetailInfoCard;
